import {
  DisableRoundingIcon,
  RoundTo1Icon,
  RoundTo2Icon,
} from "@cynthai/icons";
import { useState } from "react";

import Button from "./Button";
import { Divider } from "./Divider";
import Input from "./Input";
import Radio from "./Radio";
import RadioGroup from "./RadioGroup";

export type TableColumnSortDirection = "none" | "asc" | "desc";

export interface TableColumnFilterSort {
  direction: TableColumnSortDirection;
  onChange: (direction: TableColumnSortDirection) => void;
  labels: {
    heading: string;
    none: string;
    ascending: string;
    descending: string;
  };
}

export interface TableColumnNumberFormat {
  rounding: number | null | undefined;
  useGrouping: boolean;
  onRoundingChange: (decimals: number) => void;
  onGroupingChange: (useGrouping: boolean) => void;
}

interface NumericColumnFilter {
  kind: "number";
  format: TableColumnNumberFormat;
  range?: {
    minimum: string;
    maximum: string;
    onApply: (minimum: string, maximum: string) => void;
    onClear: () => void;
  };
  labels: {
    rounding: string;
    grouping: string;
    range: string;
    minimum: string;
    maximum: string;
    apply: string;
    clear: string;
  };
}

interface ValuesColumnFilter {
  kind: "values";
  label: string;
}

export type TableColumnFilterControlsProps = {
  sorting: TableColumnFilterSort;
} & (NumericColumnFilter | ValuesColumnFilter);

function ColumnSorting({
  sorting,
}: Readonly<{ sorting: TableColumnFilterSort }>) {
  return (
    <>
      <p className="mb-2 text-sm">{sorting.labels.heading}</p>

      <RadioGroup
        className="space-y-2 text-sm"
        aria-label={sorting.labels.heading}
        value={sorting.direction}
        onChange={(direction) => {
          if (
            direction === "none" ||
            direction === "asc" ||
            direction === "desc"
          ) {
            sorting.onChange(direction);
          }
        }}
      >
        <Radio value="none">{sorting.labels.none}</Radio>

        <Radio value="asc">{sorting.labels.ascending}</Radio>

        <Radio value="desc">{sorting.labels.descending}</Radio>
      </RadioGroup>
    </>
  );
}

function NumericColumnControls({
  sorting,
  format,
  range,
  labels,
}: Readonly<NumericColumnFilter & { sorting: TableColumnFilterSort }>) {
  const [minimum, setMinimum] = useState(range?.minimum ?? "");

  const [maximum, setMaximum] = useState(range?.maximum ?? "");

  const clear = () => {
    setMinimum("");
    setMaximum("");
    range?.onClear();
  };

  return (
    <div className="text-text-primary p-3">
      <p className="my-2 text-sm">{labels.rounding}</p>

      <div className="flex items-center gap-2">
        <Button
          size="square"
          color={format.rounding === 1 ? "primary" : "tertiary"}
          aria-label={`${labels.rounding} (0.0)`}
          onPress={() => format.onRoundingChange(1)}
        >
          <RoundTo1Icon />
        </Button>

        <Button
          size="square"
          color={format.rounding === 2 ? "primary" : "tertiary"}
          aria-label={`${labels.rounding} (0.00)`}
          onPress={() => format.onRoundingChange(2)}
        >
          <RoundTo2Icon />
        </Button>

        <Button
          size="square"
          color={format.rounding === 0 ? "primary" : "tertiary"}
          aria-label={`${labels.rounding} (0)`}
          onPress={() => format.onRoundingChange(0)}
        >
          <DisableRoundingIcon />
        </Button>

        <Button
          size="square"
          color={format.useGrouping ? "primary" : "tertiary"}
          className="text-xs"
          onPress={() => format.onGroupingChange(!format.useGrouping)}
        >
          {labels.grouping}
        </Button>
      </div>

      <Divider className="my-4" />

      <ColumnSorting sorting={sorting} />

      {range && (
        <>
          <Divider className="my-4" />

          <fieldset aria-label={labels.range}>
            <legend className="mb-2 text-sm">{labels.range}</legend>

            <div className="space-y-2">
              <Input
                type="number"
                size="sm"
                color="background"
                aria-label={labels.minimum}
                placeholder={labels.minimum}
                value={minimum}
                onChange={(event) => setMinimum(event.target.value)}
              />

              <Input
                type="number"
                size="sm"
                color="background"
                aria-label={labels.maximum}
                placeholder={labels.maximum}
                value={maximum}
                onChange={(event) => setMaximum(event.target.value)}
              />

              {(minimum !== "" || maximum !== "") && (
                <div className="mt-2 flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1"
                    onPress={() => range.onApply(minimum, maximum)}
                  >
                    {labels.apply}
                  </Button>

                  <Button
                    size="sm"
                    color="tertiary"
                    variant="ghost"
                    className="flex-1"
                    onPress={clear}
                  >
                    {labels.clear}
                  </Button>
                </div>
              )}
            </div>
          </fieldset>
        </>
      )}
    </div>
  );
}

/** Shared DataTable controls; consumers own sorting, formatting, ranges and facet data. */
export function TableColumnFilterControls(
  props: Readonly<TableColumnFilterControlsProps>,
) {
  if (props.kind === "number") {
    return <NumericColumnControls {...props} />;
  }

  return (
    <div className="text-text-primary p-3">
      <p className="text-sm">{props.label}</p>

      <Divider className="my-4" />

      <ColumnSorting sorting={props.sorting} />
    </div>
  );
}
